import type { ApiUser } from '@core';

/** RFC 4122 UUID (v1–v5) pattern for creator user id detection */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * @description Returns true when the string looks like a user id UUID from the API
 * @param value - Raw string from `createdBy` or metadata
 * @returns Whether the value should be resolved via GET `/api/users/{id}`
 */
export function isLikelyUserUuid(value: string): boolean {
    return UUID_PATTERN.test(value.trim());
}

/**
 * @description Resolves the creator user id when the API stores a UUID on the user or in metadata
 * @param user - User row from list or detail API
 * @returns Creator `userId` to fetch, or undefined when no UUID is available
 */
export function extractCreatedByUserId(user: ApiUser): string | undefined {
    const meta = user.metadata ?? {};
    const candidates: unknown[] = [
        user.createdBy,
        meta['createdByUserId'],
        meta['creatorUserId'],
        meta['createdById'],
    ];

    for (const candidate of candidates) {
        if (typeof candidate !== 'string') {
            continue;
        }
        const trimmed = candidate.trim();
        if (trimmed.length > 0 && isLikelyUserUuid(trimmed)) {
            return trimmed;
        }
    }

    return undefined;
}

/**
 * @description Builds a single display line for first and last name fields
 * @param firstName - Creator first name
 * @param lastName - Creator last name
 * @returns Trimmed full name or empty string when both are blank
 */
export function formatUserFullName(firstName?: string, lastName?: string): string {
    return `${firstName ?? ''} ${lastName ?? ''}`.trim();
}

/** Placeholder while creator name is loading (matches users table cell) */
export const CREATED_BY_LOADING_PLACEHOLDER = '…';

/**
 * @description Same rules as the users table "Created By" column: resolved name, ellipsis while loading UUID, raw label, or N/A
 * @param user - User row or detail payload
 * @param resolvedNameByCreatorId - Optional cache of creator id → display name from prior fetches
 * @returns Label to show before or without an extra GET creator call
 */
export function getCreatedByDisplayLabel(
    user: ApiUser,
    resolvedNameByCreatorId?: ReadonlyMap<string, string>,
): string {
    const creatorId = extractCreatedByUserId(user);
    if (creatorId) {
        const resolved = resolvedNameByCreatorId?.get(creatorId)?.trim();
        return resolved && resolved.length > 0 ? resolved : CREATED_BY_LOADING_PLACEHOLDER;
    }

    const raw = user.createdBy?.trim() ?? '';
    if (raw.length > 0 && !isLikelyUserUuid(raw)) {
        return raw;
    }

    return 'N/A';
}

/**
 * @description Display name after GET `/api/users/{creatorId}` (matches users list resolution)
 * @param firstName - Creator first name
 * @param lastName - Creator last name
 * @returns Full name or N/A when missing
 */
export function formatCreatedByResolvedName(firstName?: string, lastName?: string): string {
    return formatUserFullName(firstName, lastName) || 'N/A';
}
